import React, { useState, useEffect } from 'react';
import {
  Save,
  Loader2,
  AlertCircle,
  Check,
  DollarSign,
  FileCheck
} from 'lucide-react';
import { useCompany } from '../../../../contexts/CompanyContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { settingsService } from '../../../../services/settingsService';

const InvoiceSettingsTab = () => {
  const { currentCompany } = useCompany();
  const { hasPermission, userRole } = usePermissions();
  // Primary owner always has full access
  const canEdit = userRole === 'primary_owner' || userRole === 'owner' || hasPermission('manage_invoice_settings');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [settings, setSettings] = useState({
    payment_terms_text: 'Payment due within 30 days of invoice date.',
    invoice_footer_disclaimer: 'Thank you for your business.',
    default_tax_rate: 10.00
  });

  useEffect(() => {
    if (currentCompany?.id) {
      loadSettings();
    }
  }, [currentCompany?.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const { data } = await settingsService.getCompanySettings(currentCompany.id);
        setSettings({
          payment_terms_text: data?.payment_terms_text || 'Payment due within 30 days of invoice date.',
          invoice_footer_disclaimer: data?.invoice_footer_disclaimer || 'Thank you for your business.',
          default_tax_rate: data?.default_tax_rate || 10.00
        });
      } catch (e) {
        console.warn('Company settings not available:', e.message);
        // Use defaults - table may not exist yet
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      // Don't show error - use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await settingsService.updateCompanySettings(currentCompany.id, {
        payment_terms_text: settings.payment_terms_text,
        invoice_footer_disclaimer: settings.invoice_footer_disclaimer,
        default_tax_rate: parseFloat(settings.default_tax_rate) || 10.00
      });

      setSuccess('Invoice settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save invoice settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Invoice Settings</h2>
          <p className="text-text-secondary text-sm mt-1">
            Configure your invoice numbering and default text
          </p>
        </div>
        {!canEdit && (
          <span className="px-3 py-1 bg-border/50 text-text-secondary rounded-full text-xs">
            View Only
          </span>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <Check size={16} className="text-green-500" />
          <span className="text-green-400 text-sm">{success}</span>
        </div>
      )}

      {/* Tax Rate */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <DollarSign size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Tax Settings</h3>
        </div>

        <div className="max-w-xs">
          <label className="block text-text-secondary text-sm mb-2">Default Tax Rate (%)</label>
          <div className="relative">
            <input
              type="number"
              value={settings.default_tax_rate}
              onChange={(e) => setSettings({ ...settings, default_tax_rate: e.target.value })}
              disabled={!canEdit}
              min="0"
              max="100"
              step="0.5"
              className="w-full px-4 py-2 pr-8 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="10"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">%</span>
          </div>
          <p className="text-xs text-text-secondary mt-1">Australian GST is 10%</p>
        </div>
      </div>

      {/* Invoice Text */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <FileCheck size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Invoice Text</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Payment Terms</label>
            <textarea
              value={settings.payment_terms_text}
              onChange={(e) => setSettings({ ...settings, payment_terms_text: e.target.value })}
              disabled={!canEdit}
              rows={2}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              placeholder="Payment due within 30 days of invoice date."
            />
            <p className="text-xs text-text-secondary mt-1">Displayed in the payment terms section of invoices</p>
          </div>

          <div>
            <label className="block text-text-secondary text-sm mb-2">Footer Disclaimer</label>
            <textarea
              value={settings.invoice_footer_disclaimer}
              onChange={(e) => setSettings({ ...settings, invoice_footer_disclaimer: e.target.value })}
              disabled={!canEdit}
              rows={2}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              placeholder="Thank you for your business."
            />
            <p className="text-xs text-text-secondary mt-1">Displayed at the bottom of all invoices</p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoiceSettingsTab;
